import * as React from 'react';
import { List, CellMeasurer, CellMeasurerCache, ListRowProps } from 'react-virtualized';
import { style } from 'typestyle';

import { useRef, useState } from 'react';
import type { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';

export interface VirtualizedListboxProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Optional props to control width&height if needed
   */
  listWidth?: number;
  /**
   * The maximum height for the listbox.
   * If the total estimated height is less than this, the listbox will shrink to fit its content.
   * Otherwise, it will be capped at this height and scroll.
   */
  maxListHeight?: number;
  /**
   * Whether to automatically highlight the first option when opened
   */
  autoHighlight?: boolean;
  /**
   * Index of the currently highlighted item
   */
  highlightedIndex?: number;
  isMultiSelect?: boolean;
  selectedCount?: number;
  totalCount?: number;
  onToggleAll?: (selections: Suggestion[]) => void;
}

const listWrapper = style({
  $nest: {
    '& .ReactVirtualized__Grid__innerScrollContainer': {
      maxWidth: 'none !important',
    },
    '.s5-MuiAutocomplete-option div': {
      width: '100%',
      overflowWrap: 'break-word',
      letterSpacing: 'normal', // this MUST be `normal` because the text-width calcs below rely on it to be
      height: 'stretch',
    },
  },
});

interface RowWrapperProps {
  index: number;
  items: unknown[];
  style: React.CSSProperties;
  isMultiSelect: boolean;
  autoHighlight: boolean;
  highlightedIndex: number;
  registerChild: React.LegacyRef<HTMLDivElement> | undefined;
  measure: () => void;
}

const RowWrapper = ({
  index,
  items,
  style,
  isMultiSelect,
  autoHighlight,
  highlightedIndex,
  registerChild,
  measure,
}: RowWrapperProps) => {
  React.useLayoutEffect(() => {
    // as soon as we have a size for the element measure so the parent List can update
    measure();
  }, [measure]);

  // Check if this is the highlighted item
  const isHighlighted = isMultiSelect ? false : autoHighlight && index === highlightedIndex;

  // Merge provided row styles with any custom styles.
  const finalRowStyle: React.CSSProperties = {
    ...style,
    whiteSpace: 'normal',
    boxSizing: 'border-box',
    listStyle: 'none',
    backgroundColor: isHighlighted ? 'rgba(0, 168, 155, 0.45)' : undefined,
  };
  // Set aria-selected for accessibility
  return (
    <div style={finalRowStyle} aria-selected={isHighlighted} ref={registerChild}>
      {items[index]}
    </div>
  );
};

const VirtualizedListbox = React.forwardRef<HTMLUListElement, VirtualizedListboxProps>(
  function VirtualizedListbox(props, forwardedRef) {
    // Turn children into an array of items.
    const items = React.Children.toArray(props.children);
    const itemCount = items.length;

    /**
     * Create a CellMeasurerCache allowing variable row heights.
     * The minimum row height is 36, but it will expand if text wraps.
     */
    const cache = React.useMemo(() => {
      return new CellMeasurerCache({
        minHeight: 36,
        fixedWidth: true,
      });
    }, []);

    const {
      listWidth = 200,
      maxListHeight = 400,
      style: customStyle,
      autoHighlight = true,
      highlightedIndex = -1,
      isMultiSelect = false,
      ...other
    } = props;

    // Estimate the total height by summing up (or approximating) each row’s height.
    // Since the cache might not have measured all rows yet, we use the default for any unknown.
    let totalEstimatedHeight = 0;
    for (let i = 0; i < itemCount; i++) {
      // Try to get the measured row height; if missing, fallback to cache.defaultHeight.
      const rowHeight = cache.rowHeight({ index: i });
      totalEstimatedHeight += rowHeight;
    }

    // The List's height will be the smaller of the total estimated height and the maxListHeight.
    const computedHeight = Math.min(totalEstimatedHeight, maxListHeight);

    const containerStyle: React.CSSProperties = {
      ...customStyle,
      overflow: 'hidden', //let list handle scrolling
    };

    // A ref to the List so we can force it to recompute heights when children change.
    const listRef = React.useRef<List>(null);
    const [domHighlightedIndex, setDomHighlightedIndex] = useState<number>(-1);
    const listboxRef = useRef<HTMLUListElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLUListElement | null) => {
        listboxRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLUListElement | null>).current = node;
        }
      },
      [forwardedRef],
    );

    React.useLayoutEffect(() => {
      listRef.current?.recomputeRowHeights();
      listRef.current?.forceUpdateGrid();
    }, []);

    React.useEffect(() => {
      const node = listboxRef.current;
      if (!node) return;

      const updateFromDom = () => {
        const target = node.querySelector('[data-focus=\"true\"]') as HTMLElement | null;

        if (!target) {
          const itemLength = items.length;
          if (domHighlightedIndex > 0 && domHighlightedIndex < itemLength) {
            listRef.current?.scrollToRow(0);
          } else if (domHighlightedIndex === 0) {
            listRef.current?.scrollToRow(itemLength - 1);
          }
        } else {
          const attr = target.getAttribute('data-option-index');
          const idx = attr ? parseInt(attr, 10) : -1;
          if (!Number.isNaN(idx) && idx >= 0 && idx !== domHighlightedIndex) {
            setDomHighlightedIndex(idx);
            listRef.current?.scrollToRow(idx);
          }
        }
      };

      updateFromDom(); // initial sync

      const observer = new MutationObserver(updateFromDom);
      observer.observe(node, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-focus', 'aria-selected'],
      });
      return () => observer.disconnect();
    }, [domHighlightedIndex, highlightedIndex, items.length]);

    React.useEffect(() => {
      if (highlightedIndex != null && highlightedIndex >= 0) {
        listRef.current?.scrollToRow(highlightedIndex);
      }
    }, [highlightedIndex]);

    // Row renderer: wrap each row in a CellMeasurer so its height can adjust.
    function rowRenderer({ index, key, style, parent }: ListRowProps) {
      return (
        <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
          {({ registerChild, measure }) => {
            return (
              <RowWrapper
                index={index}
                items={items}
                style={style}
                isMultiSelect={isMultiSelect}
                autoHighlight={autoHighlight}
                highlightedIndex={highlightedIndex}
                registerChild={registerChild as React.LegacyRef<HTMLDivElement> | undefined}
                measure={measure}
              />
            );
          }}
        </CellMeasurer>
      );
    }
    return (
      <ul ref={setRefs} {...other} style={containerStyle}>
        <List
          ref={listRef}
          autoWidth
          width={listWidth}
          height={computedHeight}
          rowCount={itemCount}
          deferredMeasurementCache={cache}
          rowHeight={cache.rowHeight}
          rowRenderer={rowRenderer}
          overscanCount={5}
          style={{ outline: 'none' }}
          className={listWrapper}
          role="listbox"
        />
      </ul>
    );
  },
);

export default VirtualizedListbox;
